import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../config/api';
import { Plus, Edit, Trash2, Utensils, Calendar, FileText, Settings, AlertTriangle, ArrowLeft } from 'lucide-react';

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function HorsesFeedPlan() {
  const { id } = useParams(); // horse id (optional when navigating from list)
  const navigate = useNavigate();
  const [horses, setHorses] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedHorseId, setSelectedHorseId] = useState(id || '');
  const [allPlans, setAllPlans] = useState([]); // All plans across all horses
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [header, setHeader] = useState({ start_date: '', end_date: '', notes: '' });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const toDateInput = (value) => {
    if (!value) return '';
    // Accept Date, ISO string, or date-only string
    try {
      if (typeof value === 'string') {
        // If already yyyy-MM-dd, return as-is
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
        const d = new Date(value);
        if (isNaN(d.getTime())) return '';
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
      if (value instanceof Date) {
        const yyyy = value.getFullYear();
        const mm = String(value.getMonth() + 1).padStart(2, '0');
        const dd = String(value.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
      return '';
    } catch {
      return '';
    }
  };

  useEffect(() => {
    fetchHorses();
    fetchProducts();
    fetchAllPlans();
  }, []);

  useEffect(() => {
    if (selectedHorseId) {
      setShowEditModal(true);
    }
  }, [selectedHorseId]);

  useEffect(() => {
    if (selectedPlanId) {
      fetchPlan(selectedPlanId);
    } else {
      setHeader({ start_date: '', end_date: '', notes: '' });
      setItems([]);
    }
  }, [selectedPlanId]);

  const fetchHorses = async () => {
    try {
      const res = await axios.get(getApiUrl('/api/horses'));
      setHorses(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching horses:', error);
      setHorses([]);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(getApiUrl('/api/horses/feed/products'));
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  const fetchAllPlans = async () => {
    try {
      const res = await axios.get(getApiUrl('/api/horses/feed/plans'));
      setAllPlans(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching feed plans:', error);
      setAllPlans([]);
    }
  };

  const fetchPlan = async (planId) => {
    try {
      const res = await axios.get(getApiUrl(`/api/horses/feed/plans/${planId}`));
      setHeader({ start_date: toDateInput(res.data.plan.start_date) || '', end_date: toDateInput(res.data.plan.end_date) || '', notes: res.data.plan.notes || '' });
      setItems(Array.isArray(res.data.items) ? res.data.items : []);
    } catch (error) {
      console.error('Error fetching plan:', error);
      setHeader({ start_date: '', end_date: '', notes: '' });
      setItems([]);
    }
  };

  const addLine = () => {
    setItems((prev) => [...prev, { feed_product_id: '', product_display_name: '', amount: '', unit: '', frequency: 'Daily', instructions: '' }]);
  };

  const updateLine = (index, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
  };

  const removeLine = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const savePlan = async () => {
    if (!selectedHorseId) return;
    
    // Filter out items with empty feed_product_id or amount
    const validItems = items.filter(item => 
      item.feed_product_id && 
      item.feed_product_id.trim() !== '' && 
      item.amount && 
      parseFloat(item.amount) > 0
    );
    
    if (validItems.length === 0) {
      alert('Please add at least one valid feed item with a product and amount.');
      return;
    }
    
    setLoading(true);
    try {
      if (selectedPlanId) {
        await axios.put(getApiUrl(`/api/horses/feed/plans/${selectedPlanId}`), {
          horse_id: selectedHorseId,
          start_date: toDateInput(header.start_date) || null,
          end_date: toDateInput(header.end_date) || null,
          notes: header.notes || null,
          items: validItems,
        });
      } else {
        const res = await axios.post(getApiUrl('/api/horses/feed/plans'), {
          horse_id: selectedHorseId,
          start_date: toDateInput(header.start_date) || null,
          end_date: toDateInput(header.end_date) || null,
          notes: header.notes || null,
          items: validItems,
        });
        setSelectedPlanId(res.data.id);
      }
      await fetchAllPlans();
      setShowEditModal(false);
      setSelectedPlanId('');
      setSelectedHorseId('');
      setHeader({ start_date: '', end_date: '', notes: '' });
      setItems([]);
    } catch (error) {
      console.error('Error saving plan:', error);
      alert('Failed to save feed plan');
    } finally {
      setLoading(false);
    }
  };

  const editPlan = (plan) => {
    setSelectedHorseId(plan.horse_id);
    setSelectedPlanId(plan.id);
    setShowEditModal(true);
  };

  const confirmDelete = (plan) => {
    setDeletingPlan(plan);
    setShowDeleteConfirm(true);
  };

  const deletePlan = async () => {
    if (!deletingPlan) return;
    try {
      await axios.delete(getApiUrl(`/api/horses/feed/plans/${deletingPlan.id}`));
      await fetchAllPlans();
      setShowDeleteConfirm(false);
      setDeletingPlan(null);
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Failed to delete feed plan');
    }
  };

  const createProduct = async (name) => {
    if (!name || !name.trim()) return null;
    try {
      const res = await axios.post(getApiUrl('/api/horses/feed/products'), { name: name.trim() });
      setProducts((prev) => [...prev, res.data]);
      return res.data;
    } catch (error) {
      console.error('Error creating product:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      alert(`Failed to create product "${name}": ${error.response?.data?.error || error.message}`);
      return null;
    }
  };

  const handleProductSelect = async (index, value) => {
    // First check if the value exactly matches an existing product name
    const exactMatch = products.find(p => p.name === value);
    if (exactMatch) {
      // User selected from dropdown or typed exact match
      updateLine(index, 'feed_product_id', exactMatch.id);
      updateLine(index, 'product_display_name', '');
      return;
    }

    // Check if it's a case-insensitive match
    const caseInsensitiveMatch = products.find(p => p.name.toLowerCase() === value.toLowerCase());
    if (caseInsensitiveMatch) {
      updateLine(index, 'feed_product_id', caseInsensitiveMatch.id);
      updateLine(index, 'product_display_name', '');
      return;
    }

    // If no match found, user is typing something new
    // Clear any existing selection and switch to typing mode
    updateLine(index, 'feed_product_id', '');
    updateLine(index, 'product_display_name', value);
  };

  const handleProductBlur = async (index) => {
    const line = items[index];
    const displayName = line.product_display_name || '';

    // If a product is already selected, ensure display text is cleared and do nothing
    if (line.feed_product_id && String(line.feed_product_id).trim() !== '') {
      if (displayName) {
        updateLine(index, 'product_display_name', '');
      }
      return;
    }
    
    // No product selected; process typed value
    if (!displayName.trim()) {
      updateLine(index, 'feed_product_id', '');
      return;
    }

    // Check if it's an existing product
    const existingProduct = products.find(p => p.name.toLowerCase() === displayName.toLowerCase());
    if (existingProduct) {
      updateLine(index, 'feed_product_id', existingProduct.id);
      updateLine(index, 'product_display_name', '');
    } else {
      // Create new product
      const newProduct = await createProduct(displayName);
      if (newProduct) {
        updateLine(index, 'feed_product_id', newProduct.id);
        updateLine(index, 'product_display_name', '');
      } else {
        // If creation failed, try to find the product again (might have been created by another process)
        await fetchProducts();
        const retryProduct = products.find(p => p.name.toLowerCase() === displayName.toLowerCase());
        if (retryProduct) {
          updateLine(index, 'feed_product_id', retryProduct.id);
          updateLine(index, 'product_display_name', '');
        }
      }
    }
  };


  const startNewPlan = () => {
    setSelectedHorseId('');
    setSelectedPlanId('');
    setHeader({ start_date: '', end_date: '', notes: '' });
    setItems([]);
    setShowEditModal(true);
  };

  const cancelForm = () => {
    setShowEditModal(false);
    setSelectedHorseId('');
    setSelectedPlanId('');
    setHeader({ start_date: '', end_date: '', notes: '' });
    setItems([]);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl sm:text-2xl font-bold flex items-center">
            <Utensils className="w-6 h-6 mr-2" />
            Feed Plan Management
          </h2>
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={() => navigate('/horses/feed-report')}
              className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Feed Report</span>
            </button>
            <button 
              onClick={() => navigate('/horses/feed-products')}
              className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Manage Products</span>
            </button>
            <button 
              onClick={startNewPlan}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New Feed Plan</span>
            </button>
            <button 
              onClick={() => navigate('/horses')}
              className="flex items-center gap-2 px-3 py-2 border rounded hover:bg-gray-50 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Return to Horses</span>
            </button>
          </div>
        </div>
      </div>


      {/* Plans List */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">All Feed Plans</h3>
        </div>
        <div className="p-4">
          {!Array.isArray(allPlans) || allPlans.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No feed plans created yet. Click "New Feed Plan" to get started.</p>
          ) : (
            <div className="space-y-3">
              {Array.isArray(allPlans) && allPlans.map((plan) => (
                <div key={plan.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 border rounded-lg hover:bg-gray-50 gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                      <h4 className="font-medium text-gray-900 truncate">{plan.horse_name}</h4>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <span className="text-sm text-gray-500 whitespace-nowrap">
                          {plan.start_date ? formatDate(plan.start_date) : 'No start date'}
                          {plan.end_date && ` - ${formatDate(plan.end_date)}`}
                        </span>
                      </div>
                    </div>
                    {plan.notes && (
                      <p className="text-sm text-gray-600 mt-1 break-words">{plan.notes}</p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => editPlan(plan)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit plan"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => confirmDelete(plan)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Delete plan"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>


      {/* Edit Feed Plan Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[95vh] overflow-y-auto">
            <div className="p-3 sm:p-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-base sm:text-lg font-semibold truncate pr-2">{selectedPlanId ? 'Edit Feed Plan' : 'Create New Feed Plan'}</h3>
                <button onClick={cancelForm} className="text-gray-500 hover:text-gray-700 flex-shrink-0">
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-3 sm:p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Horse *</label>
                  <select 
                    className="border rounded px-3 py-2 w-full" 
                    value={selectedHorseId} 
                    onChange={(e) => setSelectedHorseId(e.target.value)}
                    required
                  >
                    <option value="">Select horse</option>
                    {horses.map((h) => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input 
                    type="date" 
                    className="border rounded px-3 py-2 w-full" 
                    value={header.start_date} 
                    onChange={(e) => setHeader({ ...header, start_date: e.target.value })} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input 
                    type="date" 
                    className="border rounded px-3 py-2 w-full" 
                    value={header.end_date} 
                    onChange={(e) => setHeader({ ...header, end_date: e.target.value })} 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <input 
                    type="text" 
                    className="border rounded px-3 py-2 w-full" 
                    value={header.notes} 
                    onChange={(e) => setHeader({ ...header, notes: e.target.value })} 
                    placeholder="Optional notes about this plan"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium">Feed Items</h4>
                  <button 
                    onClick={addLine} 
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Add Item
                  </button>
                </div>
                
                <div className="space-y-4">
                  {items.map((line, idx) => (
                    <div key={idx} className="border p-3 sm:p-4 rounded bg-gray-50">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Product *</label>
                          <input
                            type="text"
                            className={`border rounded px-2 py-1 w-full ${
                              !line.feed_product_id || line.feed_product_id.trim() === '' 
                                ? 'border-red-300 bg-red-50' 
                                : 'border-gray-300'
                            }`}
                            placeholder="Type product name..."
                            value={line.feed_product_id ? products.find(p => p.id === line.feed_product_id)?.name || '' : (line.product_display_name || '')}
                            onChange={(e) => handleProductSelect(idx, e.target.value)}
                            onBlur={() => handleProductBlur(idx)}
                            list={`products-edit-${idx}`}
                          />
                          <datalist id={`products-edit-${idx}`}>
                            {products.filter(p => p.is_active).map((p) => (
                              <option key={p.id} value={p.name} />
                            ))}
                          </datalist>
                          {(!line.feed_product_id || line.feed_product_id.trim() === '') && (
                            <p className="text-xs text-red-600 mt-1">Please select or create a product</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Amount *</label>
                          <input 
                            className={`border rounded px-2 py-1 w-full ${
                              !line.amount || parseFloat(line.amount) <= 0 
                                ? 'border-red-300 bg-red-50' 
                                : 'border-gray-300'
                            }`}
                            type="number" 
                            placeholder="0" 
                            value={line.amount} 
                            onChange={(e) => updateLine(idx, 'amount', e.target.value)} 
                          />
                          {(!line.amount || parseFloat(line.amount) <= 0) && (
                            <p className="text-xs text-red-600 mt-1">Please enter a valid amount</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
                          <input 
                            className="border rounded px-2 py-1 w-full" 
                            type="text" 
                            placeholder="kg, lb, etc." 
                            value={line.unit || ''} 
                            onChange={(e) => updateLine(idx, 'unit', e.target.value)} 
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
                          <select 
                            className="border rounded px-2 py-1 w-full" 
                            value={line.frequency || 'Daily'} 
                            onChange={(e) => updateLine(idx, 'frequency', e.target.value)}
                          >
                            <option>Daily</option>
                            <option>Twice Daily</option>
                            <option>Weekly</option>
                            <option>As Needed</option>
                          </select>
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Instructions</label>
                        <input 
                          className="border rounded px-2 py-1 w-full" 
                          type="text" 
                          placeholder="Special instructions..." 
                          value={line.instructions || ''} 
                          onChange={(e) => updateLine(idx, 'instructions', e.target.value)} 
                        />
                      </div>
                      <div className="flex justify-end mt-3">
                        <button 
                          className="text-red-600 text-sm hover:bg-red-50 px-3 py-1 rounded" 
                          onClick={() => removeLine(idx)}
                        >
                          Remove Item
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {items.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Utensils className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p>No feed items added yet. Click "Add Item" to get started.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button 
                  onClick={cancelForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={savePlan} 
                  disabled={loading || !selectedHorseId || items.length === 0} 
                  className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 hover:bg-blue-700"
                >
                  {loading ? 'Saving...' : (selectedPlanId ? 'Update Plan' : 'Create Plan')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold">Confirm Delete</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the feed plan for <span className="font-semibold">{deletingPlan.horse_name}</span>? 
              This action cannot be undone and will remove all associated feed items.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingPlan(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deletePlan}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


